import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";

type StepRecord = { journeyStep?: unknown; journeyDepth?: unknown };

/**
 * Mirrors a journey's step into the browser history, so the browser's back
 * button steps back through the journey instead of leaving it.
 *
 * The step owner stays the source of truth: the history entry only records
 * which step was showing. A browser back to an EARLIER step is handed to
 * `onBrowserBack`, which the owner applies through its own transitions; an
 * entry naming a later step (browser forward, or a reload after the journey's
 * in-memory inputs were lost) is never entered. It is rewritten to the step
 * the owner actually shows, which is the earliest step whose inputs exist.
 *
 * This hook writes nothing but the step name and a depth, keeping whatever
 * state the entry already holds: it adds no amount, phone number or email.
 */
export const useStepHistory = <S extends string>(
  step: S,
  steps: readonly S[],
  onBrowserBack: (target: S) => void
) => {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const handledKey = useRef<string | null>(null);
  // The entry a step was pushed from, until the router shows the new one.
  const pushedFrom = useRef<string | null>(null);

  const record = (location.state ?? {}) as StepRecord;
  const recorded = steps.indexOf(record.journeyStep as S);
  const depth = typeof record.journeyDepth === "number" ? record.journeyDepth : 0;
  const current = steps.indexOf(step);

  useEffect(() => {
    // The router shows each move in a transition, so a Back pressed before
    // a push is shown lands on the entry already shown, under a new POP.
    const undidPush =
      navigationType === "POP" && pushedFrom.current === location.key;
    if (pushedFrom.current !== null && pushedFrom.current !== location.key) {
      pushedFrom.current = null;
    }
    const arrived = handledKey.current !== location.key || undidPush;
    handledKey.current = location.key;
    if (undidPush) pushedFrom.current = null;
    if (recorded === current) return;

    // A pop onto an entry from before the journey recorded anything is a
    // return to its first step.
    const target = recorded === -1 ? 0 : recorded;
    if (arrived && navigationType === "POP" && target < current) {
      onBrowserBack(steps[target]);
      return;
    }

    const advanced = !arrived && recorded !== -1 && current > recorded;
    if (advanced) pushedFrom.current = location.key;
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      {
        replace: !advanced,
        state: {
          ...(location.state as object | null),
          journeyStep: step,
          journeyDepth: advanced ? depth + 1 : depth,
        },
      }
    );
    // `onBrowserBack` is the owner's callback; re-running on its identity
    // alone would replay a pop that was already applied.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, location.key, navigationType]);

  // The in-journey Back button walks the same history the browser does, so
  // the two can never disagree about where "back" leads. On the first entry
  // the journey recorded there is nothing to pop, and the owner steps back.
  return useCallback(
    (fallback: () => void) => {
      if (recorded === current && depth > 0) navigate(-1);
      else fallback();
    },
    [recorded, current, depth, navigate]
  );
};

export default useStepHistory;
